"use client";

import * as React from "react";
import { useEffect } from "react";
import { Trash2, AlertTriangle, User, Users } from "lucide-react";
import { hasTicketAgeRange, ticketAgeRangeLabel, ticketAgeWarning, ticketAgeWarningMessage } from "@obby/lib";
import { cn } from "@baluu/ui/lib/utils";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { Button } from "@baluu/ui/components/button";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Textarea } from "@baluu/ui/components/textarea";
import { DateField } from "@baluu/ui/components/date-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@baluu/ui/components/select";
import {
  StarRatingInput,
  CurrencyInput,
  DateTimeInput,
  UrlInput,
  MultiSelectCheckboxGroup,
  AddressFieldInput,
} from "./field-inputs";
import {
  setBuyerSlot,
  moveBuyerToTicket,
  padToSlots,
  padToGroups,
  isSlotEmpty,
  fieldsForSelfCard,
  linkedAnswersFor,
  buildSelfSeedAnswers,
} from "./utils/participant-seed";

// Re-exported here because `@baluu/ui/components/checkout/*` only resolves
// .tsx files, and the dashboard needs these to render a self record.
export {
  resolveLinkedCustomerSlugClient,
  fieldsForSelf,
} from "./utils/participant-seed";
import { singularizeParticipantLabel } from "../../lib/participant-label";
import type { TicketSeatGroup } from "./utils/ticket-seat-groups";

export type CheckoutParticipantField = {
  id: string;
  slug: string;
  label: string;
  fieldType: string;
  helpText: string | null;
  isRequired: boolean;
  options: string[];
  /**
   * The customer field this question is "the same as" for a buyer who is
   * attending, or null when it is theirs to answer as an attendee. Linked
   * questions never appear on the buyer's own card — their answer already
   * lives on their customer record.
   */
  linkedCustomerSlug?: string | null;
};

export type CheckoutParticipant = {
  id?: string;
  /**
   * The buyer's own "I'm attending" seat. Sent to the API as `{ isSelf: true }`
   * and resolved there to this customer's own attendee record — the client
   * never names it, so a tampered id cannot attach a stranger.
   */
  isSelf?: boolean;
  answers: Record<string, unknown>;
  /**
   * The buyer's answers to LINKED questions, keyed by CUSTOMER slug, for the
   * basket page to show as already collected. Presentation only: the server
   * recomputes the merge from `answers`.
   */
  linkedAnswers?: Record<string, unknown>;
  /** The ticket this seat was bought under (set by the step from `ticketGroups`). */
  ticketTypeId?: string;
};

export type ExistingParticipant = {
  id: string;
  /** The buyer's own record: offered through "I'm attending", never the saved-attendee picker. */
  isSelf?: boolean;
  displayName: string;
  values: Record<string, string>;
};

export type ParticipantBuyerInfo = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
};

/** One entry of the API `participants` payload (existing → id only; inline → answers). */
export type ParticipantPayloadEntry = {
  id?: string;
  isSelf?: boolean;
  answers: Array<{ slug: string; value: unknown }>;
  linkedAnswers?: Record<string, unknown>;
  /**
   * The seat this person fills, counted from 0 across the whole order in the
   * order the tickets are sent. Lets the API seat everyone where the shopper
   * put them even though empty seats aren't sent. Optional: older widget
   * bundles don't send it, and the API then seats by position.
   */
  seatIndex?: number;
  /** The ticket type of that seat, so the API can still find the right seat if its order differs. */
  ticketTypeId?: string;
};

export type ParticipantsStepProps = {
  participantLabel: string; // e.g. "Children"
  fields: CheckoutParticipantField[];
  existing: ExistingParticipant[];
  maxParticipants: number;
  /**
   * The order's seats grouped by ticket (`buildTicketSeatGroups`). When
   * given, seats are listed under their ticket and the buyer picks which
   * ticket is theirs; their total should equal `maxParticipants`. Omit for
   * flows without tickets (bundles), which list plain numbered seats.
   */
  ticketGroups?: TicketSeatGroup[];
  value: CheckoutParticipant[];
  onChange: (v: CheckoutParticipant[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  /** When true, pre-add the buyer as the first attendee (school setting). */
  buyerIsAttendee?: boolean;
  /** The buyer's entered identity, mapped onto the built-in attendee slugs. */
  buyer?: ParticipantBuyerInfo;
  /**
   * Persistent "already auto-seeded the buyer" flag, owned by the checkout hook
   * so it survives this step unmounting/remounting on Back/Next. Without it a
   * per-mount ref would re-add the buyer after the shopper removed them.
   */
  buyerSeededRef?: React.MutableRefObject<boolean>;
  /**
   * What the buyer has already answered about themselves elsewhere in this
   * basket, so a second listing doesn't ask again. Optional: without it each
   * item stands alone and the server still reconciles at charge time.
   */
  selfSeed?: Record<string, unknown>;
  onSelfAnswersChange?: (answers: Record<string, unknown>) => void;
  /**
   * "Name later" listing (plans-done/booking-seats-whos-coming.md, decision 6):
   * places left completely empty are fine; the buyer names them afterwards
   * from the "Who's coming?" link. A place that is started must still be
   * finished.
   */
  allowUnnamed?: boolean;
  /**
   * The day attendees' ages are checked on against their ticket's age range
   * (booking seats Phase 5): the (first) session's start. Without it no age
   * warning is shown. Soft only: the step stays valid whatever the age.
   */
  sessionDate?: string | Date | null;
  className?: string;
};

/**
 * The age warning for one seat, or null: the person's date of birth (typed
 * here, else their saved record) against their ticket's range on the day.
 * Pure, so the checkout and its tests agree.
 */
export function seatAgeWarningText(
  p: CheckoutParticipant,
  group: Pick<TicketSeatGroup, "minAge" | "maxAge"> | undefined,
  fields: CheckoutParticipantField[],
  existing: ExistingParticipant[],
  sessionDate: string | Date | null | undefined,
  name?: string | null,
): string | null {
  if (!sessionDate || !group || !hasTicketAgeRange(group)) return null;
  const saved = p.id ? existing.find((e) => e.id === p.id) : undefined;
  for (const f of fields) {
    if (f.fieldType !== "dateOfBirth") continue;
    const typed = p.answers[f.slug];
    const dob = typeof typed === "string" && typed ? typed : saved?.values[f.slug];
    if (typeof dob !== "string" || !dob) continue;
    const warning = ticketAgeWarning(group, dob, sessionDate);
    return warning ? ticketAgeWarningMessage(warning, name ?? saved?.displayName ?? null) : null;
  }
  return null;
}

function AgeWarning({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="status" className="flex items-start gap-1.5 text-sm text-amber-700">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{text} You can still book; the school may ask to change the ticket.</span>
    </p>
  );
}

/**
 * Flatten the step's value to the API item payload shape
 * (`participants: { id?, answers: {slug,value}[] }[]`). Existing (id-bearing)
 * participants are still resolved server-side by id — their `answers` here
 * carry only THIS listing's missing-required-field completion, merged into
 * the saved record by the server (never used to re-resolve identity). Empty
 * seats are dropped — validation only lets one through when the profile
 * requires nothing (or for a bundle, which has no dated seat to name) — and so
 * is the buyer's own seat marker: the buyer attends via their booking
 * (identity from checkout), not as a separate attendee record.
 */
export function participantsToPayload(
  value: CheckoutParticipant[],
): ParticipantPayloadEntry[] {
  const out: ParticipantPayloadEntry[] = [];
  value.forEach((p, seatIndex) => {
    if (p.isSelf !== true && isSlotEmpty(p)) return;
    const answers = Object.entries(p.answers).map(([slug, v]) => ({ slug, value: v }));
    // Where this person sits: their position in the full list, empty seats
    // included, so dropping an empty seat no longer moves everyone after it.
    const seat = { seatIndex, ...(p.ticketTypeId ? { ticketTypeId: p.ticketTypeId } : {}) };
    // The buyer's seat carries no id: the server resolves it from the order's
    // customer, so sending one would be meaningless at best.
    if (p.isSelf) {
      out.push({
        isSelf: true,
        answers,
        ...(p.linkedAnswers && Object.keys(p.linkedAnswers).length
          ? { linkedAnswers: p.linkedAnswers }
          : {}),
        ...seat,
      });
      return;
    }
    out.push(p.id ? { id: p.id, answers, ...seat } : { answers, ...seat });
  });
  return out;
}

/** `content` placed in `seat`, keeping the seat's ticket. */
function withSeatTicket(seat: CheckoutParticipant, content: CheckoutParticipant): CheckoutParticipant {
  return seat.ticketTypeId ? { ...content, ticketTypeId: seat.ticketTypeId } : content;
}

function isBlank(value: unknown): boolean {
  return (
    value === "" ||
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  );
}

/** One inline field control, matching the canonical checkout field types. */
export function ParticipantFieldControl({
  field,
  value,
  onChange,
}: {
  field: CheckoutParticipantField;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  switch (field.fieldType) {
    case "longText":
      return (
        <Textarea
          value={(value as string) ?? ""}
          placeholder={field.helpText ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "checkbox":
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!value}
            onCheckedChange={(c) => onChange(c === true)}
          />
          {field.helpText && (
            <span className="text-sm text-muted-foreground">{field.helpText}</span>
          )}
        </div>
      );
    case "select":
      return (
        <Select value={(value as string) ?? ""} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          {/* container={null} renders inline (no body portal) so the dropdown
              works inside the checkout overlay AND the webcomponents Shadow DOM;
              z-[10002] keeps it above the overlay — matches the other checkout
              steps (user-form-step, personal-details). */}
          <SelectContent container={null} className="z-[10002]">
            {field.options.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "multiSelect":
      return (
        <MultiSelectCheckboxGroup
          options={field.options}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(v) => onChange(v)}
        />
      );
    case "rating":
      return (
        <StarRatingInput value={(value as string) ?? ""} onChange={onChange} />
      );
    case "currency":
      return (
        <CurrencyInput value={(value as string) ?? ""} onChange={onChange} />
      );
    case "dateTime":
      return (
        <DateTimeInput value={(value as string) ?? ""} onChange={onChange} />
      );
    case "url":
      return <UrlInput value={(value as string) ?? ""} onChange={onChange} />;
    case "date":
    case "dateOfBirth":
      return (
        <DateField
          value={(value as string) ?? ""}
          onChange={onChange}
          mode={field.fieldType === "dateOfBirth" ? "dateOfBirth" : "date"}
          // Checkout also renders inside the webcomponents Shadow DOM, where a
          // body-portalled popover would be invisible.
          inShadowDom
        />
      );
    case "address":
      // Same component and same comma-joined storage as the buyer side, so an
      // address means one thing across both surfaces.
      return (
        <AddressFieldInput
          value={(value as string) ?? ""}
          onChange={(next) => onChange(next)}
        />
      );
    default:
      return (
        <Input
          type={
            field.fieldType === "email"
              ? "email"
              : field.fieldType === "number"
                ? "number"
                : field.fieldType === "phone"
                  ? "tel"
                  : "text"
          }
          value={(value as string) ?? ""}
          placeholder={field.helpText ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

/**
 * True when an inline (new) participant has all required answers.
 *
 * There is deliberately no special-cased name rule. The built-in
 * `p_first_name` field is deletable and its required flag editable (only the
 * customer email is locked), so a school that swapped it for a custom text
 * field — e.g. a Kids Profile asking "Child name" — has no `p_first_name` at
 * all; demanding that slug made the attendee step impossible to complete and
 * left Continue permanently disabled. The generic loop below still enforces
 * the name for schools that kept it required, which mirrors the server rule in
 * CustomerParticipant.service.ts (name enforced only while required; display
 * name otherwise falls back to the first required text answer). A filled seat
 * always carries at least one detail — `isSlotEmpty` guarantees it — matching
 * the server's "each attendee needs at least one detail" floor.
 */
export function isInlineComplete(
  fields: CheckoutParticipantField[],
  answers: Record<string, unknown>,
): boolean {
  return fields.every((f) => {
    if (!f.isRequired) return true;
    // A required checkbox (e.g. a consent box) must be ticked — `false` is a
    // real, non-blank value but doesn't satisfy "required".
    if (f.fieldType === "checkbox") return answers[f.slug] === true;
    return !isBlank(answers[f.slug]);
  });
}

/**
 * Required fields a saved attendee still needs for THIS listing's field set —
 * satisfied by a stored value (checkbox: the string "true") or a slot answer
 * typed in the completion form.
 */
export function missingRequiredForSaved(
  fields: CheckoutParticipantField[],
  saved: ExistingParticipant | undefined,
  slotAnswers: Record<string, unknown>,
): CheckoutParticipantField[] {
  if (!saved) return [];
  return fields.filter((f) => {
    if (!f.isRequired) return false;
    const stored = saved.values[f.slug];
    if (f.fieldType === "checkbox" ? stored === "true" : !isBlank(stored)) return false;
    const a = slotAnswers[f.slug];
    return f.fieldType === "checkbox" ? a !== true : isBlank(a);
  });
}

/**
 * Whether the attendee step is complete: EVERY purchased seat must satisfy the
 * profile's required fields.
 *
 * A seat left blank used to be accepted ("paid but not yet named"), which
 * silently overrode the school's own configuration — it marked a field
 * required, rendered a `*` against it, and the step accepted a blank seat
 * anyway. Schools that need a complete register (an after-school club knowing
 * which children attend) had no way to demand one, and the `*` promised
 * enforcement that did not exist.
 *
 * A school that genuinely sells seats before knowing who fills them expresses
 * that the same way it expresses everything else about attendees: by marking
 * the fields optional, on a per-listing attendance profile if only some
 * listings work that way. With no required field, every seat — blank
 * included — is trivially valid and the empty ones are dropped from the
 * payload. Bundles keep their own escape hatch (a credit pool has no dated
 * seat to name); see `use-checkout-bundle.ts`.
 */
export function areParticipantsValid(
  fields: CheckoutParticipantField[],
  existing: ExistingParticipant[],
  value: CheckoutParticipant[],
  opts: { allowUnnamed?: boolean } = {},
): boolean {
  return value.every((p) => {
    // A "name later" listing takes an empty place as it is (it is dropped
    // from the payload and named from the "Who's coming?" link).
    if (opts.allowUnnamed && isSlotEmpty(p)) return true;
    // Judge the buyer's seat against exactly what their card asks: everything
    // except the identity questions, which come from the buyer block of the
    // order and are never on the card.
    if (p.isSelf) return isInlineComplete(fieldsForSelfCard(fields), p.answers);
    if (p.id) {
      return (
        missingRequiredForSaved(fields, existing.find((e) => e.id === p.id), p.answers)
          .length === 0
      );
    }
    return isInlineComplete(fields, p.answers);
  });
}

export function ParticipantsStep({
  participantLabel,
  fields,
  existing,
  maxParticipants,
  ticketGroups,
  value,
  onChange,
  onValidationChange,
  buyerIsAttendee = false,
  buyer,
  buyerSeededRef,
  selfSeed,
  onSelfAnswersChange,
  allowUnnamed = false,
  sessionDate,
  className,
}: ParticipantsStepProps) {
  const singular = singularizeParticipantLabel(participantLabel).toLowerCase();
  const singularLabel = singularizeParticipantLabel(participantLabel);

  // Buyer-as-attendee: the buyer chooses whether they attend via an explicit
  // "I'm attending" toggle (default ON in buyer + attendees mode). A ref guard,
  // persisted across this step's remounts by the checkout hook, seeds their slot
  // once and prevents silently re-adding after they toggle off. Waits for the
  // buyer's details when the participants step precedes personal details.
  const localSeededRef = React.useRef(false);
  const seededBuyerRef = buyerSeededRef ?? localSeededRef;
  const buyerAttending = value.some((p) => p.isSelf);
  const buyerName = [buyer?.firstName, buyer?.lastName]
    .filter((v) => typeof v === "string" && v.trim() !== "")
    .join(" ");
  const hasEmptySlot = value.some((p) => isSlotEmpty(p));
  const filled = value.filter((p) => !isSlotEmpty(p));

  // The buyer's own card asks only what their customer record doesn't already
  // answer; their saved record supplies the opening values, and anything they
  // typed on another basket item wins over it.
  const selfFields = fieldsForSelfCard(fields);
  const selfEntry = value.find((p) => p.isSelf);
  const savedSelf = existing.find((e) => e.isSelf);
  const selfSeedAnswers = buildSelfSeedAnswers(fields, savedSelf?.values, selfSeed);

  // One slot per purchased seat: keep the list exactly `maxParticipants` long,
  // laid out as the ticket groups' seats when the order has tickets.
  const groups = ticketGroups?.length ? ticketGroups : undefined;
  useEffect(() => {
    const padded = groups ? padToGroups(value, groups) : padToSlots(value, maxParticipants);
    if (padded !== value) onChange(padded);
  }, [value, maxParticipants, groups, onChange]);

  // Default the buyer to attending: their seat is theirs (details come from
  // checkout — no attendee form). Runs once via a ref persisted across this
  // step's remounts, so it never re-adds after the shopper opts out.
  useEffect(() => {
    if (!buyerIsAttendee || seededBuyerRef.current) return;
    seededBuyerRef.current = true;
    if (!buyerAttending) {
      onChange(setBuyerSlot(value, true, selfSeedAnswers, maxParticipants, groups));
    }
  }, [buyerIsAttendee, buyerAttending, value, maxParticipants, groups, onChange, seededBuyerRef, selfSeedAnswers]);

  function onToggleBuyerAttending(checked: boolean) {
    seededBuyerRef.current = true; // the shopper has now decided — respect it
    onChange(setBuyerSlot(value, checked, checked ? selfSeedAnswers : {}, maxParticipants, groups));
  }

  // The buyer picks their ticket only when there is a choice to make.
  const showBuyerTicket = !!groups && groups.length > 1;
  function onBuyerTicketChange(ticketTypeId: string) {
    onChange(moveBuyerToTicket(value, ticketTypeId));
  }

  // The saved self record and the shared basket answers both arrive from
  // fetches that can resolve AFTER the seat was seeded, and the seed above runs
  // once — so top up any question the buyer hasn't answered yet when they do.
  // Each key is applied at most once, so clearing a field stays cleared.
  const appliedSeedKeysRef = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!selfEntry) return;
    const pending = Object.entries(selfSeedAnswers).filter(
      ([slug, v]) =>
        !appliedSeedKeysRef.current.has(slug) &&
        isBlank(selfEntry.answers[slug]) &&
        !isBlank(v),
    );
    if (!pending.length) return;
    for (const [slug] of pending) appliedSeedKeysRef.current.add(slug);
    const answers = { ...selfEntry.answers, ...Object.fromEntries(pending) };
    onChange(
      value.map((p) =>
        p.isSelf ? { ...p, answers, linkedAnswers: linkedAnswersFor(fields, answers) } : p,
      ),
    );
  }, [selfEntry, selfSeedAnswers, value, fields, onChange]);

  // Answers on the buyer's own card. `linkedAnswers` rides along keyed by
  // customer slug so the basket page can show those questions as collected
  // rather than asking them a second time.
  function updateSelf(slug: string, v: unknown) {
    const next = value.map((p) => {
      if (!p.isSelf) return p;
      const answers = { ...p.answers, [slug]: v };
      return { ...p, answers, linkedAnswers: linkedAnswersFor(fields, answers) };
    });
    onChange(next);
    const self = next.find((p) => p.isSelf);
    if (self) onSelfAnswersChange?.(self.answers);
  }

  const isValid = areParticipantsValid(fields, existing, value, { allowUnnamed });

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  function updateSlot(i: number, slug: string, v: unknown) {
    onChange(
      value.map((p, idx) =>
        idx === i ? { ...p, answers: { ...p.answers, [slug]: v } } : p,
      ),
    );
  }

  // A seat keeps its ticket whoever fills it.
  function assignSaved(i: number, id: string) {
    onChange(value.map((p, idx) => (idx === i ? withSeatTicket(p, { id, answers: {} }) : p)));
  }

  // Free a seat back to an empty slot (clears a saved pick or typed details).
  function clearSlot(i: number) {
    onChange(value.map((p, idx) => (idx === i ? withSeatTicket(p, { answers: {} }) : p)));
  }

  const usedSavedIds = new Set(
    value.filter((p) => p.id).map((p) => p.id as string),
  );
  // The buyer's own record belongs to the "I'm attending" toggle, not the
  // picker — offering it would let them take two seats as themselves.
  const availableSaved = existing.filter((e) => !e.isSelf && !usedSavedIds.has(e.id));

  // One seat's card. `title` numbers the seats that get a form (the buyer's
  // own seat is rendered above, under the toggle), within its ticket.
  // The seat's ticket's age range against the person in it (soft).
  function ageNote(p: CheckoutParticipant): string | null {
    const group = groups?.find((g) => g.ticketTypeId === p.ticketTypeId);
    return seatAgeWarningText(p, group, fields, existing, sessionDate);
  }

  function renderSeat(p: CheckoutParticipant, i: number, title: string) {

      // A saved attendee occupying this seat — compact row with a clear action.
      if (p.id) {
        const saved = existing.find((e) => e.id === p.id);
        return (
          <div key={`slot-${i}`} className="rounded-lg border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-muted-foreground">{title}</div>
                  <div className="font-medium truncate">
                    {saved?.displayName || `Saved ${singular}`}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => clearSlot(i)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {(() => {
              // Fields THIS listing requires that the saved record doesn't
              // already have a stored value for. Deliberately ignores slot
              // answers (unlike missingRequiredForSaved, which validates) so
              // an input stays mounted — and keeps its focus/typed value —
              // while the shopper is filling it in.
              const toComplete = fields.filter((f) => {
                if (!f.isRequired) return false;
                const stored = saved?.values[f.slug];
                return f.fieldType === "checkbox" ? stored !== "true" : isBlank(stored);
              });
              if (!toComplete.length) return null;
              return (
                <div className="space-y-3 border-t pt-3">
                  <p className="text-sm text-muted-foreground">
                    A few details are needed for this {singular}:
                  </p>
                  {toComplete.map((f) => (
                    <div key={f.id} className="space-y-1.5">
                      <Label>
                        {f.label}
                        <span className="text-destructive"> *</span>
                      </Label>
                      <ParticipantFieldControl
                        field={f}
                        value={p.answers[f.slug]}
                        onChange={(v) => updateSlot(i, f.slug, v)}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
            <AgeWarning text={ageNote(p)} />
          </div>
        );
      }

      return (
        <div key={`slot-${i}`} className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">
              {title}
            </span>
            {!isSlotEmpty(p) && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => clearSlot(i)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          {isSlotEmpty(p) && availableSaved.length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Users className="h-4 w-4 text-primary shrink-0" />
                Use one of your saved {participantLabel.toLowerCase()}
              </div>
              <Select onValueChange={(id) => assignSaved(i, id)}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder={`Select a saved ${singular}…`} />
                </SelectTrigger>
                {/* container={null} renders inline (no body portal) so the
                    dropdown works inside the checkout overlay AND the
                    webcomponents Shadow DOM; z-[10002] keeps it above the
                    overlay — matches the other checkout steps. */}
                <SelectContent container={null} className="z-[10002]">
                  {availableSaved.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.displayName || "Unnamed"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                …or fill in a new {singular} below.
              </p>
            </div>
          )}

          {fields.map((f) => (
            <div key={f.id} className="space-y-1.5">
              <Label>
                {f.label}
                {f.isRequired && <span className="text-destructive"> *</span>}
              </Label>
              <ParticipantFieldControl
                field={f}
                value={p.answers[f.slug]}
                onChange={(v) => updateSlot(i, f.slug, v)}
              />
            </div>
          ))}
          <AgeWarning text={ageNote(p)} />
        </div>
      );

  }

  // Seat numbers skip the buyer, and restart under each ticket heading.
  function seatTitles(indices: number[]): Map<number, string> {
    const titles = new Map<number, string>();
    let n = 0;
    for (const i of indices) {
      if (value[i]?.isSelf) continue;
      titles.set(i, `${singularLabel} ${++n}`);
    }
    return titles;
  }

  return (
    <div className={cn("space-y-5", className)}>
      <div>
        <h3 className="text-lg font-semibold">Who is attending?</h3>
        <p className="text-sm text-muted-foreground">
          {maxParticipants > 1
            ? `Add the ${participantLabel.toLowerCase()} attending — one per seat.`
            : `Add the ${singular} attending.`}
        </p>
        {allowUnnamed && (
          <p className="text-sm text-muted-foreground">
            Don&apos;t know yet? Leave a place empty and tell us who&apos;s coming later. We&apos;ll email
            you a link.
          </p>
        )}
      </div>

      {buyerIsAttendee && (
        <div className="space-y-3">
          <label className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer">
            <Checkbox
              checked={buyerAttending}
              disabled={!buyerAttending && !hasEmptySlot}
              onCheckedChange={(c) => onToggleBuyerAttending(c === true)}
            />
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">
              I&apos;m attending{buyerName ? ` — ${buyerName}` : ""}
            </span>
          </label>

          {buyerAttending && showBuyerTicket && (
            <div className="space-y-1.5">
              <Label>Your ticket</Label>
              <Select
                value={selfEntry?.ticketTypeId ?? ""}
                onValueChange={onBuyerTicketChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your ticket…" />
                </SelectTrigger>
                {/* container={null}: inline, so it works in the Shadow DOM. */}
                <SelectContent container={null} className="z-[10002]">
                  {groups!.map((g) => (
                    <SelectItem key={g.ticketTypeId} value={g.ticketTypeId}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {buyerAttending && selfFields.length > 0 && (
            <div className="rounded-lg border p-4 space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">
                  You{buyerName ? ` — ${buyerName}` : ""}
                </span>
                <p className="text-xs text-muted-foreground">
                  Your name and contact details come from your booking. We
                  won&apos;t ask any of this twice.
                </p>
              </div>
              {selfFields.map((f) => (
                <div key={f.id} className="space-y-1.5">
                  <Label>
                    {f.label}
                    {f.isRequired && <span className="text-destructive"> *</span>}
                  </Label>
                  <ParticipantFieldControl
                    field={f}
                    value={selfEntry?.answers[f.slug]}
                    onChange={(v) => updateSelf(f.slug, v)}
                  />
                </div>
              ))}
              {selfEntry && <AgeWarning text={ageNote(selfEntry)} />}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {groups
          ? groups.map((g) => {
              const indices = value
                .map((_, i) => i)
                .filter((i) => value[i]!.ticketTypeId === g.ticketTypeId);
              const titles = seatTitles(indices);
              const holdsBuyer = indices.some((i) => value[i]!.isSelf);
              return (
                <div key={g.ticketTypeId} className="space-y-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <h4 className="font-semibold">
                      {g.label}
                      {hasTicketAgeRange(g) && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          {ticketAgeRangeLabel(g)}
                        </span>
                      )}
                    </h4>
                    <span className="text-sm text-muted-foreground">
                      {g.seatCount === 1 ? "1 seat" : `${g.seatCount} seats`}
                      {holdsBuyer ? " · includes you" : ""}
                    </span>
                  </div>
                  {indices.map((i) => {
                    const title = titles.get(i);
                    return title ? renderSeat(value[i]!, i, title) : null;
                  })}
                </div>
              );
            })
          : (() => {
              const titles = seatTitles(value.map((_, i) => i));
              return value.map((p, i) => {
                const title = titles.get(i);
                return title ? renderSeat(p, i, title) : null;
              });
            })()}
      </div>

      {maxParticipants > 1 && (
        <p className="text-sm text-muted-foreground">
          {filled.length} of {maxParticipants} added
        </p>
      )}
    </div>
  );
}
